/* eslint-disable max-lines */
import enc from "crypto-js/enc-hex";
import SHA256 from "crypto-js/sha256";
import addMonths from "date-fns/addMonths";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import first from "lodash/first";
import type { NextPageContext, PageProps } from "next";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { GraphQLTaggedNode } from "react-relay";
import { graphql, readInlineData, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { scmpPlus } from "scmp-sdk";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { data as appData, article as articleData } from "shared/data";
import { section as sectionData } from "shared/data/section";
import { Edition } from "shared/lib/edition";
import { theme } from "shared/lib/styles";
import { isAfterOrEqual, isBeforeOrEqual, notEmpty, parseQueryString } from "shared/lib/utils";

import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "scmp-app/components/advertisement/ad-slots/types";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { HomeV2ClientQueryLoader } from "scmp-app/components/home-v2/client-query-loader";
import { HomeV2Newsletter } from "scmp-app/components/home-v2/newsletter";
import { type Section } from "scmp-app/components/home-v2/section-top";
import { HomeV2SuperSplash } from "scmp-app/components/home-v2/super-splash";
import { HomeV2TrendingTopic } from "scmp-app/components/home-v2/trending-topic";
import { HeroTopStories } from "scmp-app/components/home/hero-top-stories";
import { HomeSeo } from "scmp-app/components/home/seo";
import { usePlusWidgetConfig } from "scmp-app/components/plus/plus-widget/hooks";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import {
  useAutoRefreshWhenIdlingForTenMinutes,
  useGetEntityIdsByQueueItems,
} from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { homeNativeArticleContentItemContent$key } from "scmp-app/queries/__generated__/homeNativeArticleContentItemContent.graphql";
import type {
  homePageQuery,
  LiveWidgetEditionEnum,
} from "scmp-app/queries/__generated__/homePageQuery.graphql";
import type { homeRecommendedForYouSponsorArticleQueueItemsEdge$key } from "scmp-app/queries/__generated__/homeRecommendedForYouSponsorArticleQueueItemsEdge.graphql";
import type { topStoriesHomeV2QueueItemsEdge$key } from "scmp-app/queries/__generated__/topStoriesHomeV2QueueItemsEdge.graphql";

import { useNewHomepageSurveyContext } from "./contexts";
import { usePickMorningStudioArticles, usePremiumNativeAd, useTopStoriesTracking } from "./hooks";
import { HERO_TOP_STORIES_COUNT } from "./proteus/constants";
import { useProteusTopStories } from "./proteus/hooks";
import {
  AdvertisersWidget,
  AIWidget1,
  AIWidget2,
  AroundWidget,
  CFTWidget,
  Container,
  ContentContainer,
  ContentWrapper,
  DesktopBannerAds,
  DesktopSidebarAdsBottom,
  DesktopSidebarAdsTop,
  Divider1,
  Divider2,
  Divider3,
  FlexiUnitWidget,
  LatestWidget,
  LatestWidgetAds,
  MobileFooterAds,
  MostPopularWidget,
  MostPopularWidgetAds,
  MultimediaWidget,
  MultimediaWidgetAds,
  NewsletterWidget,
  OpenQuestionWidget,
  OpinionWidget,
  OpinionWidgetAds,
  PremiumAds,
  SCMPShortsWidget,
  SectionBlock1,
  SectionBlock2,
  SectionBlock3,
  SectionBlock4,
  SectionBlock5,
  SeriesWidget,
  SpotlightWidget,
  StyledAdSlot,
  StyledAppFooter,
  StyledContentItemHomeNativeArticle,
  StyledTakeoverAdSlot,
  SubscriptionWidget,
  ThingsToDoWidget,
  ThingsToDoWidgetAds,
  TopStoriesAds,
} from "./styles";

const homePageQuery: GraphQLTaggedNode = graphql`
  query homePageQuery(
    $applicationId: String!
    $chinaFutureTechSectionEntityId: String!
    $chinaFutureTechStoryPackageArticlesQueueName: String!
    $commentHarrySectionIds: [String]
    $commentQueueName: String!
    $dailyPulseTypeUuId: String!
    $focusQueueName: String!
    $highlightQueueName: String!
    $latestExcludeArticleTypes: [String]
    $latestExcludeSectionIds: [String]
    $liveWidgetEditionEnumValue: LiveWidgetEditionEnum!
    $morningStudioQueueName: String!
    $plusNewsAgendaEndDate: Timestamp!
    $plusNewsAgendaStartDate: Timestamp!
    $plusNewsAgendaTypes: [String]!
    $scmpPlusPaywallTypeIds: [String]
    $scmpShortsQueueName: String!
    $secondaryStoryPackageArticlesQueueName: String!
    $seriesWidgetArticlesQueueName: String!
    $spotlightQueueName: String!
    $storyPackageArticlesQueueName: String!
    $topStoriesQueueName: String!
  ) {
    topStories: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          ...heroSecondaryArticlesHomeV2QueueItemsEdge
          ...topStoriesHomeV2QueueItemsEdge
          node {
            ... on Content {
              entityId
            }
            ...heroArticlesHomeV2Content
          }
        }
      }
    }
    morningStudioQueue: queue(filter: { name: $morningStudioQueueName }) {
      ...hooksHomeV2PickMorningStudioArticle
    }
    ...latestContentsHomeV2Query
      @arguments(
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        latestExcludeSectionIds: $latestExcludeSectionIds
        latestExcludeArticleTypes: $latestExcludeArticleTypes
      )
    ...thingsToDoHomeV2Query
    ...commentQueueQuery
      @arguments(
        commentQueueName: $commentQueueName
        commentHarrySectionIds: $commentHarrySectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
    ...opinionHomeV2QueueQuery
      @arguments(
        commentQueueName: $commentQueueName
        commentHarrySectionIds: $commentHarrySectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
    ...seriesWidgetHomeV2Query
      @arguments(seriesWidgetArticlesQueueName: $seriesWidgetArticlesQueueName)
    ...secondaryStoryPackageHomeV2Query
      @arguments(secondaryStoryPackageArticlesQueueName: $secondaryStoryPackageArticlesQueueName)
    ...chinaFutureTechStoryPackageHomeV2Query
      @arguments(
        chinaFutureTechSectionEntityId: $chinaFutureTechSectionEntityId
        chinaFutureTechStoryPackageArticlesQueueName: $chinaFutureTechStoryPackageArticlesQueueName
      )
    ...spotlightHomeV2Query @arguments(spotlightQueueName: $spotlightQueueName)
    ...scmpShortsHomeV2Query @arguments(scmpShortsQueueName: $scmpShortsQueueName)
    ...focusArticleHomeFocusArticleQuery @arguments(focusQueueName: $focusQueueName)
    ...flexiUnitHomeV2QueueQuery @arguments(focusQueueName: $focusQueueName)
    ...superSplashHomeV2Query @arguments(edition: $liveWidgetEditionEnumValue)
    ...plusWidgetQuery
      @arguments(
        applicationId: $applicationId
        dailyPulseTypeUuId: $dailyPulseTypeUuId
        highlightQueueName: $highlightQueueName
        plusNewsAgendaEndDate: $plusNewsAgendaEndDate
        plusNewsAgendaStartDate: $plusNewsAgendaStartDate
        plusNewsAgendaTypes: $plusNewsAgendaTypes
      )
    ...chinaScienceDiscoveryWidgetQuery
    ...hooksPlusWidgetConfigQuery
    ...plusWidgetHomeV2Query
    ...hooksPremiumNativeAdConfigQuery

    commentQueue: queue(filter: { name: $commentQueueName }) {
      items(first: 5) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    topStoriesEntityIds: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    storyPackageArticles: queue(filter: { name: $storyPackageArticlesQueueName }) {
      items(first: 3) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    spotlightQueue: queue(filter: { name: $spotlightQueueName }) {
      items(first: 4) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    secondaryStoryPackageArticles: queue(
      filter: { name: $secondaryStoryPackageArticlesQueueName }
    ) {
      items(first: 4) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
  }
`;

const homeRecommendedForYouSponsorArticleFragment = graphql`
  fragment homeRecommendedForYouSponsorArticleQueueItemsEdge on QueueItemsEdge @inline {
    node {
      ... on Content {
        ...homeNativeArticleContentItemContent
      }
    }
  }
`;

// Per-edition ordering of the first/second/fourth section blocks, same as v1
// (the third block is always china and the fifth always style).
const sectionBlocksByEdition: Record<
  Edition,
  { first: Section; fourth: Section; second: Section }
> = {
  [Edition.Asia]: { first: "twia", fourth: "lifestyle", second: "people-culture" },
  [Edition.HongKong]: { first: "hong-kong", fourth: "wellness", second: "lifestyle" },
  [Edition.International]: { first: "hong-kong", fourth: "people-culture", second: "lifestyle" },
};

const HomePage: RelayComponent<PageProps, homePageQuery> = ({ preloadedQuery, randomSeed }) => {
  const queryReference = usePreloadedQuery(homePageQuery, preloadedQuery);

  const { query: routeQuery } = useRouter();
  const edition = routeQuery.edition as Edition;
  const adZone = useMemo(() => `homepage_${edition}/home`, [edition]);

  useAutoRefreshWhenIdlingForTenMinutes();

  const ssrContentEntityIds = useGetEntityIdsByQueueItems([
    ...(queryReference.commentQueue?.items?.edges ?? []),
    ...(queryReference.secondaryStoryPackageArticles?.items?.edges ?? []),
    ...(queryReference.spotlightQueue?.items?.edges ?? []),
    ...(queryReference.storyPackageArticles?.items?.edges ?? []),
    ...(queryReference.topStoriesEntityIds?.items?.edges ?? []),
  ]);

  const {
    slot1: morningStudioArticleSlot1,
    slot2: morningStudioArticleSlot2,
    slot3: morningStudioArticleSlot3,
  } = usePickMorningStudioArticles(
    randomSeed ?? "fallback seed",
    queryReference.morningStudioQueue,
  );
  const nativeAdsReference = first(
    [morningStudioArticleSlot1, morningStudioArticleSlot2].filter(notEmpty),
  );
  const nativeAdsReferenceData =
    readInlineData<homeRecommendedForYouSponsorArticleQueueItemsEdge$key>(
      homeRecommendedForYouSponsorArticleFragment,
      nativeAdsReference ?? null,
    ) ?? null;

  // The Top Stories queue is shared with the hero widgets: edges[0] is the lead
  // hero article, edges[1..2] are the hero's secondary row, and edges[3..17]
  // (15 articles, given the query's `first: 18`) feed the Top Stories block in
  // order. The sponsored morning-studio article renders as a native-ad
  // side-card inside Top Stories (see HomeV2TopStories) rather than occupying
  // one of the article rows.
  const queueEdges = (queryReference.topStories?.items?.edges ?? [])
    .filter(notEmpty)
    .filter(edge => notEmpty(edge.node));
  const heroEdge = first(queueEdges);
  const heroSecondaryEdges = queueEdges.slice(1, 3);
  const heroTailEdges = queueEdges.slice(3, HERO_TOP_STORIES_COUNT);
  const legacyTopStories6to18 = queueEdges.slice(HERO_TOP_STORIES_COUNT, 18);
  // TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
  // Positions 4-5 from the SSR legacy queue, prepended to positions 6-18.
  const legacyTopStoriesEdges = [
    ...heroTailEdges,
    ...legacyTopStories6to18,
  ] as unknown as topStoriesHomeV2QueueItemsEdge$key;

  const { plusWidgetConfig } = usePlusWidgetConfig(queryReference);
  // The highlight widget is subscriber-only (gated by `hasScmpPlusAccessRight`
  // inside HomeV2PlusWidget), so the relevant disable schedule is `withAccess`.
  const isPlusDisabled = useMemo(
    () =>
      plusWidgetConfig?.withAccess?.disabledDateRanges?.some(({ end, start }) => {
        const now = new Date();
        return isAfterOrEqual(now, start) && (notEmpty(end) ? isBeforeOrEqual(now, end) : true);
      }) ?? false,
    [plusWidgetConfig],
  );

  const isEnabledPremiumNativeAd = usePremiumNativeAd(queryReference);
  const { typeformElement } = useNewHomepageSurveyContext();

  // TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
  // top_story1 tracking lives in the page (not HeroTopStories) so it fires
  // exactly once regardless of how many times HeroTopStories mounts/unmounts
  // during the Proteus Suspense fallback → resolve cycle.
  const legacyListOfArticleIds = [
    heroEdge?.node?.entityId,
    ...heroSecondaryEdges.map(({ node }) => node?.entityId),
    ...(
      legacyTopStoriesEdges as unknown as ReadonlyArray<{ node: null | { entityId?: string } }>
    ).map(({ node }) => node?.entityId),
  ].filter(notEmpty);
  const { captureTrackImpressionEventElement1: captureHeroImpressionReference } =
    useTopStoriesTracking(legacyListOfArticleIds);

  // TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
  // `topStoriesEdges` is the only value that differs between the legacy and
  // Proteus paths; everything else is passed to HeroTopStories as props.
  // `isImpressionSuppressed` defaults to true — only allow top_story1 (hero)
  // impressions until we're certain the user is NOT in the Proteus variant
  // (optimizely resolved + variant 0) or Proteus content has resolved.
  const recommendedForYouSponsorArticleReference =
    readInlineData<homeRecommendedForYouSponsorArticleQueueItemsEdge$key>(
      homeRecommendedForYouSponsorArticleFragment,
      morningStudioArticleSlot3 ?? null,
    );

  // TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
  // Only HeroTopStories needs to swap legacy/Proteus edges — keep it inside
  // the Suspense boundary. All other widgets render once at the page level.
  const renderHeroTopStories = (
    topStoriesEdges: topStoriesHomeV2QueueItemsEdge$key,
    isImpressionSuppressed = true,
  ) => (
    <HeroTopStories
      adZone={adZone}
      heroEdge={heroEdge}
      heroRef={captureHeroImpressionReference}
      heroSecondaryEdges={heroSecondaryEdges}
      isImpressionSuppressed={isImpressionSuppressed}
      isPlusDisabled={isPlusDisabled}
      nativeAdsReferenceData={nativeAdsReferenceData}
      queryReference={queryReference}
      topStoriesEdges={topStoriesEdges}
    />
  );

  // TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
  const { legacyIsImpressionSuppressed, proteusElement } = useProteusTopStories({
    heroTailEdges,
    legacyTopStoriesEdges,
    renderHeroTopStories,
    topStoriesQueueName: preloadedQuery?.variables?.topStoriesQueueName,
  });
  return (
    <>
      {typeformElement}
      <HomeSeo edition={edition} />
      <Container>
        <ContentContainer>
          <HomeV2SuperSplash reference={queryReference} />
          <ContentWrapper $edition={edition} $hasPremiumNativeAd={isEnabledPremiumNativeAd}>
            {proteusElement ??
              renderHeroTopStories(legacyTopStoriesEdges, legacyIsImpressionSuppressed)}

            <ErrorBoundary fallback={<></>}>
              <NewsletterWidget>
                <HomeV2Newsletter />
              </NewsletterWidget>
            </ErrorBoundary>

            <FlexiUnitWidget reference={queryReference} />
            <OpinionWidget reference={queryReference} />
            <LatestWidget reference={queryReference} />
            <ThingsToDoWidget reference={queryReference} />

            <SeriesWidget reference={queryReference} />
            <OpenQuestionWidget reference={queryReference} />
            <CFTWidget reference={queryReference} />
            <SCMPShortsWidget reference={queryReference} />
            <SpotlightWidget reference={queryReference} />

            <TopStoriesAds>
              <StyledAdSlot
                adUnit="m_banner1"
                breakpoint={theme.breakpoints.only("mobile")}
                sizes={[
                  [300, 100],
                  [320, 100],
                  [300, 50],
                  [320, 50],
                  [300, 250],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withLabelBackground
                zone={adZone}
              />
              <StyledAdSlot
                adUnit="m_lrec1"
                breakpoint={theme.breakpoints.only("tablet")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withLabelBackground
                zone={adZone}
              />
              <StyledAdSlot
                adUnit="d_lrec1"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                zone={adZone}
              />
            </TopStoriesAds>

            {isEnabledPremiumNativeAd && (
              <PremiumAds>
                <StyledAdSlot
                  adUnit="m_native2f"
                  breakpoint={theme.breakpoints.down("desktop")}
                  sizes={[[1, 1], "fluid"]}
                  targeting={{ paid: TargetingPaidType.NotArticle }}
                  zone={adZone}
                />
                <StyledAdSlot
                  adUnit="d_native2f"
                  breakpoint={theme.breakpoints.up("desktop")}
                  sizes={[[1, 1], "fluid"]}
                  targeting={{ paid: TargetingPaidType.NotArticle }}
                  zone={adZone}
                />
              </PremiumAds>
            )}

            <OpinionWidgetAds>
              <StyledAdSlot
                adUnit="m_lrec3"
                breakpoint={theme.breakpoints.down("desktop")}
                sizes={[
                  [300, 250],
                  [320, 480],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withLabelBackground
                zone={adZone}
              />
              <StyledAdSlot
                adUnit="d_lrec3"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, 600],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                withMaxContainerSize={false}
                zone={adZone}
              />
            </OpinionWidgetAds>

            <DesktopSidebarAdsTop>
              <StyledAdSlot
                adUnit="d_lrec3a"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, 600],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                zone={adZone}
              />
            </DesktopSidebarAdsTop>

            <DesktopSidebarAdsBottom>
              <StyledAdSlot
                adUnit="d_lrec3b"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                zone={adZone}
              />
            </DesktopSidebarAdsBottom>

            <LatestWidgetAds>
              <StyledAdSlot
                adUnit="m_lrec3c"
                breakpoint={theme.breakpoints.down("desktop")}
                sizes={[
                  [300, 250],
                  [320, 480],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withLabelBackground
                zone={adZone}
              />
              <StyledAdSlot
                adUnit="d_lrec2"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, 600],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                withMaxContainerSize={false}
                zone={adZone}
              />
            </LatestWidgetAds>

            <ThingsToDoWidgetAds>
              <StyledAdSlot
                adUnit="m_lrec2"
                breakpoint={theme.breakpoints.down("desktop")}
                sizes={[
                  [300, 250],
                  [320, 480],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withLabelBackground
                zone={adZone}
              />
              <StyledAdSlot
                adUnit="d_lrec4"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, 600],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                zone={adZone}
              />
            </ThingsToDoWidgetAds>

            <DesktopBannerAds>
              <StyledAdSlot
                adUnit="d_banner5"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [970, 250],
                  [728, 90],
                  [970, 90],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                zone={adZone}
              />
            </DesktopBannerAds>

            <Divider1 />

            <ErrorBoundary fallback={<></>}>
              <ClientSideSuspense fallback={<></>}>
                <HomeV2ClientQueryLoader ssrContentEntityIds={ssrContentEntityIds}>
                  {({ aiWidgets, data, existingEntityIds }) => {
                    const sectionBlocks = sectionBlocksByEdition[edition];
                    const advertisersReference = data.advertisers?.items;

                    const hasRecommendedForYou =
                      aiWidgets[AiEngineDataType.HomeRecommendedForYou].entityIds.length > 0;

                    const hasYouMayHaveMissed =
                      aiWidgets[AiEngineDataType.HomeYouMayHaveMissed].entityIds.length > 0;

                    return (
                      <>
                        {hasRecommendedForYou && (
                          <AIWidget1
                            aiEngineResponse={aiWidgets[AiEngineDataType.HomeRecommendedForYou]}
                            slots={{
                              sponsorArticle(tracking, ref) {
                                return (
                                  <StyledContentItemHomeNativeArticle
                                    onClick={tracking}
                                    ref={ref}
                                    reference={
                                      recommendedForYouSponsorArticleReference?.node as unknown as homeNativeArticleContentItemContent$key
                                    }
                                  />
                                );
                              },
                            }}
                            variant={AiEngineDataType.HomeRecommendedForYou}
                          />
                        )}
                        {hasYouMayHaveMissed && (
                          <AIWidget2
                            aiEngineResponse={aiWidgets[AiEngineDataType.HomeYouMayHaveMissed]}
                            slots={{
                              nativeAds: (
                                <>
                                  <StyledAdSlot
                                    adUnit="d_native2a"
                                    breakpoint={theme.breakpoints.up("desktop")}
                                    customizedAdUnitTargeting="dnative-6"
                                    sizes={[[1, 1], "fluid"]}
                                    targeting={{ paid: TargetingPaidType.NotArticle }}
                                    zone={adZone}
                                  />
                                  <StyledAdSlot
                                    adUnit="m_native2a"
                                    breakpoint={theme.breakpoints.down("desktop")}
                                    customizedAdUnitTargeting="dnative-6"
                                    sizes={[[1, 1], "fluid"]}
                                    targeting={{ paid: TargetingPaidType.NotArticle }}
                                    zone={adZone}
                                  />
                                </>
                              ),
                            }}
                            variant={AiEngineDataType.HomeYouMayHaveMissed}
                          />
                        )}
                        {advertisersReference && (
                          <AdvertisersWidget reference={advertisersReference} />
                        )}
                        <AroundWidget existingEntityIds={existingEntityIds} />
                        <MostPopularWidget
                          reference={data}
                          trendingTopicSlot={<HomeV2TrendingTopic reference={data} />}
                        />
                        <MultimediaWidget reference={data} />
                        <SubscriptionWidget />
                        <SectionBlock1 reference={data} section={sectionBlocks.first} />
                        <SectionBlock2 reference={data} section={sectionBlocks.second} />
                        <SectionBlock3 reference={data} section="china" />
                        <SectionBlock4 reference={data} section={sectionBlocks.fourth} />
                        <SectionBlock5 reference={data} section="style" />
                        <MultimediaWidgetAds>
                          <StyledAdSlot
                            adUnit="m_lrec3g"
                            breakpoint={theme.breakpoints.down("desktop")}
                            sizes={[
                              [300, 250],
                              [320, 480],
                            ]}
                            targeting={{
                              paid: TargetingPaidType.NotArticle,
                            }}
                            withBackground
                            withLabel
                            withLabelBackground
                            zone={adZone}
                          />
                          <StyledAdSlot
                            adUnit="d_lrec4a"
                            breakpoint={theme.breakpoints.up("desktop")}
                            sizes={[[300, 250]]}
                            targeting={{
                              paid: TargetingPaidType.NotArticle,
                            }}
                            withLabel
                            withLabelBackground
                            withMaxContainerSize={false}
                            zone={adZone}
                          />
                        </MultimediaWidgetAds>
                        <MostPopularWidgetAds>
                          <StyledAdSlot
                            adUnit="m_lrec4"
                            breakpoint={theme.breakpoints.down("desktop")}
                            sizes={[
                              [300, 250],
                              [320, 480],
                            ]}
                            targeting={{
                              paid: TargetingPaidType.NotArticle,
                            }}
                            withBackground
                            withLabel
                            withLabelBackground
                            zone={adZone}
                          />
                          <StyledAdSlot
                            adUnit="d_lrec4b"
                            breakpoint={theme.breakpoints.up("desktop")}
                            sizes={[
                              [300, 250],
                              [300, 600],
                            ]}
                            targeting={{
                              paid: TargetingPaidType.NotArticle,
                            }}
                            withLabel
                            withLabelBackground
                            withMaxContainerSize={false}
                            zone={adZone}
                          />
                        </MostPopularWidgetAds>
                        <MobileFooterAds>
                          <StyledAdSlot
                            adUnit="m_lrec3h"
                            breakpoint={theme.breakpoints.only("mobile")}
                            sizes={[
                              [300, 250],
                              [320, 480],
                            ]}
                            targeting={{
                              paid: TargetingPaidType.NotArticle,
                            }}
                            withBackground
                            withLabel
                            withLabelBackground
                            zone={adZone}
                          />
                        </MobileFooterAds>
                        <Divider2 />
                        <Divider3 />
                      </>
                    );
                  }}
                </HomeV2ClientQueryLoader>
                <StyledAppFooter />
              </ClientSideSuspense>
            </ErrorBoundary>
          </ContentWrapper>

          <StyledTakeoverAdSlot adSlotProps={{ zone: adZone }} variant="home" />
        </ContentContainer>
      </Container>
    </>
  );
};

HomePage.displayName = "HomePage";

const getPageProps = (context: NextPageContext) => {
  const edition = parseQueryString(context.query.edition) as Edition | undefined;
  const adZone = `homepage_${edition ?? "int"}/home`;

  const pageProps: PageProps = {
    appBarConfiguration: {
      desktopBreakpoint: "desktop",
      hasDesktopAd: true,
      hasMobileAd: true,
      hasTabletAd: false,
      topBannerAdSlot: {
        desktop: {
          adUnit: "d_banner1",
          sizes: [
            [970, 250],
            [728, 90],
            [970, 90],
          ],
          targeting: {
            hpto: TargetingHomePageTakeOverType.Top,
            paid: TargetingPaidType.NotArticle,
          },
          zone: adZone,
        },
        mobile: {
          adUnit: "m_banner3",
          sizes: [
            [300, 100],
            [320, 100],
            [300, 50],
            [320, 50],
          ],
          targeting: {
            hpto: TargetingHomePageTakeOverType.Top,
            paid: TargetingPaidType.NotArticle,
          },
          zone: adZone,
        },
      },
      variant: "scmp/home",
    },
    initEdition: edition,
    isBot: context.query.isBot === "1",
  };

  return pageProps;
};

const generateRandomSeed = () => SHA256(new Date().toString()).toString(enc);

export default withRelay(HomePage, homePageQuery, {
  clientSideProps: context => ({
    ...getPageProps(context),
    randomSeed: generateRandomSeed(),
  }),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: async context => {
    const pageProps = Promise.resolve(getPageProps(context));
    return {
      ...(await pageProps),
      randomSeed: generateRandomSeed(),
    };
  },
  variablesFromContext(context) {
    const edition = parseQueryString(context.query.edition) ?? "int";
    const liveWidgetEditionEnumValue: LiveWidgetEditionEnum =
      edition.toLocaleUpperCase() as Uppercase<Edition>;
    const startDate = startOfDay(new Date()).getTime();
    const endDate = addMonths(endOfDay(new Date()), 1).getTime();

    return {
      applicationId: appData.application.scmp.entityId,
      chinaFutureTechSectionEntityId: sectionData.chinaFutureTech.entityId,
      chinaFutureTechStoryPackageArticlesQueueName: `section_top_${sectionData.chinaFutureTech.entityId}`,
      commentHarrySectionIds: [sectionData.comment.harrysView.entityUuid],
      commentQueueName: `opinion_top_${edition}`,
      dailyPulseTypeUuId: articleData.types.dailyPulseBriefing.entityUuid,
      edition,
      focusQueueName: `homepage_focus_${edition}`,
      highlightQueueName: "scmp_plus_home_news",
      latestExcludeArticleTypes: [
        articleData.types.series.entityUuid,
        articleData.types.letters.entityUuid,
        articleData.types.recap.entityUuid,
      ],
      latestExcludeSectionIds: [
        sectionData.posties.entityUuid,
        sectionData.spark.entityUuid,
        sectionData.youngPost.entityUuid,
        sectionData.yp.entityUuid,
        sectionData.recap.entityUuid,
        sectionData.cooking.entityUuid,
      ],
      liveWidgetEditionEnumValue,
      morningStudioQueueName: `homepage_morning_studio_${edition}`,
      plusNewsAgendaEndDate: endDate,
      plusNewsAgendaStartDate: startDate,
      plusNewsAgendaTypes: [scmpPlus.topicType.newsAgenda],
      scmpPlusPaywallTypeIds: [scmpPlus?.article?.paywallTypeId],
      scmpShortsQueueName: "homepage_video_shorts",
      secondaryStoryPackageArticlesQueueName: `homepage_package_top_2_${edition}`,
      seriesWidgetArticlesQueueName: `homepage_series_widget_${edition}`,
      spotlightQueueName: `scmp_signatures_top_${edition}`,
      storyPackageArticlesQueueName: `homepage_package_top_${edition}`,
      topStoriesQueueName: `homepage_top_${edition}`,
    };
  },
});
